/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import {
    format,
    Heading,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import { ProductComponent } from '../../themes/dlvry-adventureworks/views/components/product.component';
import {
    getPriceForProductSearchResult,
    PriceForProductSearchResultInput,
    OrderTemplate,
    QueryResultSettingsProxy,
    GetOrderTemplatesInput,
    getOrderTemplates
} from '@msdyn365-commerce-modules/retail-actions';
import { ProductList, ProductSearchResult, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { IDlvryRecentlyViewedProductsData } from './dlvry-recently-viewed-products.data';
import { IDlvryRecentlyViewedProductsProps, layout } from './dlvry-recently-viewed-products.props.autogenerated';
import { GetFullProductsByIdsInput } from './actions/get-full-products-by-ids';
import PQueue from 'p-queue';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { onBuyHasAccessValidator } from '../../shared-utils/attributes/on-buy-access-validator';
import { getCustomerAgreements } from '../../shared-utils/agreement-product-utils';
import { ProductSearchResultExt } from '../../shared-utils/types';
import { getProductListsByProductIdsAsync } from '../../actions/get-product-lists-by-product-ids';

export interface IProductCollectionViewProps extends IDlvryRecentlyViewedProductsProps<{}> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
}

export interface IProductCollectionState {
    orderTemplates: OrderTemplate[] | undefined;
    retrievingActivePrices: boolean;
    simpleProducts: SimpleProduct[];
    unitConversions: UnitOfMeasureConversion[];
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}

/**
 * ProductCollection component
 */
class ProductCollection extends React.PureComponent<IDlvryRecentlyViewedProductsProps<IDlvryRecentlyViewedProductsData>, IProductCollectionState> {

    private readonly _cartQueue: PQueue;

    constructor(props: IDlvryRecentlyViewedProductsProps<IDlvryRecentlyViewedProductsData>) {
        super(props);
        this.state = {
            orderTemplates: undefined,
            retrievingActivePrices: true,
            simpleProducts: [],
            unitConversions: []
        };
        this._cartQueue = new PQueue({ concurrency: 1 });
    }

    public async componentDidMount(): Promise<void> {
        void this._getActivePrices();
        void this._getSimpleProducts();
        void this._getUnitConversions();
        void this._getProductListsByProductIds();
    }

    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const { fullProductsSearchResultsWithCount } = this.props.data;
        const { resources } = this.props;
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingComponent = heading && (
            <Heading className='ms-product-collection__heading' headingTag={heading.tag} text={heading.text} />
        );

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: resources.flipperPrevious,
                flipperNextLabel: resources.flipperNext,
                parentId: this.props.id
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items'
            },
            isCarousel: isCarousel,
            // tslint:disable-next-line: max-line-length
            products:
                fullProductsSearchResultsWithCount &&
                fullProductsSearchResultsWithCount.result &&
                fullProductsSearchResultsWithCount.result.products.length > 0 &&
                fullProductsSearchResultsWithCount.result.products.map((item: ProductSearchResult, index: number) =>
                    this._getProduct(item, index)
                )
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }

    private _retrieveOrderTemplates: () => Promise<OrderTemplate[]> = async () => {
        return this.state.orderTemplates || this._onUpdateOrderTemplates();
    };

    private _onUpdateOrderTemplates: (product?: ProductSearchResultExt, newOrderTemplate?: ProductList) => Promise<OrderTemplate[]> = async (product, newOrderTemplate) => {
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(this.state.orderTemplates ? this.state.orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: this.props.context.request,
                config: this.props.config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, this.props.context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        this.setState({ orderTemplates: newOrderTemplates });
        return newOrderTemplates;
    };

    private _getProduct = (product: ProductSearchResult, index: number): IProductComponentViewProps | null => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const {
            context,
            config: { imageSettings },
            data: { orgInformation },
            resources
        } = this.props;

        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item',
                role: 'listitem',
                key: index
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={format(resources.ratingAriaLabel, product.AverageRating, 5)}
                    depositSuffixText={resources.depositSuffixText}
                    priceNotLoggedInText={resources.priceNotLoggedInText}
                    productIndex={index}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{
                        cart: this.props.data.cart.result,
                        product: product,
                        customerInformation: this.props.data.customerInformation.result,
                        customerAgreements: getCustomerAgreements(context, orgInformation.result),
                        simpleProduct: this.state.simpleProducts.find(x => x.RecordId === product.RecordId),
                        unitConversions: this.state.unitConversions.filter(x => x.ProductRecordId === product.RecordId)
                    }}
                    cartQueue={this._cartQueue}
                    disableBuyButton={this.state.retrievingActivePrices}
                    showPrice={!this.state.retrievingActivePrices}
                    retrieveOrderTemplates={this._retrieveOrderTemplates}
                    onUpdateOrderTemplates={this._onUpdateOrderTemplates}
                    addToCartText={resources.addToCartText}
                    updateCartText={resources.updateCartText}
                    removeFromCartText={resources.removeFromCartText}
                    outOfStockText={resources.outOfStockText}
                    priceErrorText={resources.priceErrorText}
                    toastStrings={{
                        errorHeader: resources.toastErrorHeader,
                        productsAddedToCart: resources.productsAddedToCart,
                        productsRemovedFromCart: resources.productsRemovedFromCart,
                        productsUpdatedInCart: resources.productsUpdatedInCart,
                        goToCart: resources.toastGoToCart,
                        noAccessToProduct: resources.toastNoAccessToProduct
                    }}
                    addToOrderTemplateResources={{
                        addToOrderTemplateHeader: resources.addToOrderTemplateHeader,
                        noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
                        noOrderTemplatesDescription: resources.noOrderTemplatesDescription,
                        createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
                        createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
                        cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,
                        selectTemplatesText: resources.selectTemplatesText,
                        addToTemplateButtonText: resources.addToTemplateButtonText,
                        lineItemsText: resources.lineItemsText,
                        createOrderTemplateHeader: resources.createOrderTemplateHeader,
                        orderTemplateTitle: resources.orderTemplateTitle,
                        orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
                        createOrderTemplateDescription: resources.createOrderTemplateDescription,
                        defaultOrderTemplateName: resources.defaultOrderTemplateName,
                        createOrderTemplateButtonText: resources.createOrderTemplateButtonText,
                        cancelNewOrderTemplateCreationButtonText: resources.cancelNewOrderTemplateCreationButtonText,
                        viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
                        continueShoppingButtonText: resources.continueShoppingButtonText,
                        itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
                        itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
                        itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
                        priceFree: resources.priceFree,
                        originalPriceText: resources.originalPriceText,
                        currentPriceText: resources.currentPriceText,
                        duplicatedProductsHeader: resources.duplicatedProductsHeader,
                        duplicatedProductsDescription: resources.duplicatedProductsDescription,
                        updateQuantityButtonText: resources.updateQuantityButtonText,
                        cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText,
                        addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                        addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip
                    }}
                    onBuyValidate={(product) => onBuyHasAccessValidator(
                        this.props.context,
                        this.props.data.customerInformation.result,
                        product,
                        this.props.resources.toastNoAccessToProduct
                    )}
                />
            )
        };
    };

    private _getActivePrices = async (): Promise<void> => {
        const { context } = this.props;
        const fullProductsSearchResultsWithCount = await this.props.data.fullProductsSearchResultsWithCount;
        const maxRecentlyViewedItemsCount = this.props.context.app.config.maxRecentlyViewedItemsCount;

        const productsList = fullProductsSearchResultsWithCount.products ?? [];
        const products = await getPriceForProductSearchResult(new PriceForProductSearchResultInput(productsList), context.actionContext);
        fullProductsSearchResultsWithCount.products = products;
        context.actionContext.update(new GetFullProductsByIdsInput(maxRecentlyViewedItemsCount), fullProductsSearchResultsWithCount);

        this.setState({ retrievingActivePrices: false });
    };

    private _getSimpleProducts = async(): Promise<void> => {
        const { context } = this.props;
        const fullProductsSearchResultsWithCount = await this.props.data.fullProductsSearchResultsWithCount;
        const simpleProducts = await getByIdsAsync({ callerContext: context.actionContext },
            context.request.apiSettings.channelId,
            fullProductsSearchResultsWithCount.products.map(x => x.RecordId));
        this.setState({ simpleProducts });
    };

    private _getUnitConversions = async(): Promise<void> => {
        const { actionContext, request, app } = this.props.context;

        if (!request.user.isAuthenticated || app.config.unitOfMeasureConversions !== true) {
            return;
        }

        const products = (await this.props.data.fullProductsSearchResultsWithCount).products;
        const ids = products.map(x => x.RecordId);

        if (!ids || ids.length === 0) {
            return;
        }

        const conversions = await getProductUnitOfMeasureOptionsAsync({ callerContext: actionContext }, ids);

        this.setState({ unitConversions: conversions });
    };

    private _getProductListsByProductIds = async (): Promise<void> => {
        const { context } = this.props;

        if (!context.request.user.isAuthenticated) {
            return;
        }

        const products = (await this.props.data.fullProductsSearchResultsWithCount).products;
        const ids = products.map(x => x.RecordId);

        if (!ids || ids.length === 0) {
            return;
        }

        const result = await getProductListsByProductIdsAsync({ callerContext: context.actionContext }, ids);

        products.forEach(product => {
            (product as ProductSearchResultExt).PartOfProductLists = result.filter(x => x.ProductId === product.RecordId);
        });
    };
}

export default ProductCollection;
